import { v4 as uuidv4 } from "uuid";
import { _uniqueName } from "./utils";
import { Notification } from "./Notification";

/**
 * Constructor for the Controller object. This is the frontend representation
 * of a MachineMotion controller. Axes and IOs will associate themselves with
 * the unique ID present in this data structure.
 *
 * @param {Config}              pConfig Config object that contains the Controller.
 * @param {Object | undefined}  pData JSON object from which the controller will inflate.
 *                              If not present, the controller will be populated with
 *                              default data.
 */
export function Controller(pConfig, pData) {
  Notification.call(this);

  if (pConfig === undefined) {
    console.error('Cannot initialize a Controller without a Config.');
    return;
  }

  this.mConfig            = pConfig;
  this.mManager           = pConfig.getConfigManager();
  this.mName              = this.getDefaultName();
  this.mUuid              = uuidv4();
  this.mIpAddress         = this.getDefaultIpAddress();
  this.mHardwareVersion   = this.mManager.listAvailableHardwareVersions()[0]; // Default to a vanilla V2
  this.mIsControllerAvailable = undefined; // this logic will be handled in the execution-engine in the future

  pData && this.fromJson(pData);
}

Controller.prototype = Object.create(Notification.prototype);
Controller.prototype.constructor = Controller;

Controller.prototype.fromJson = function(pData) {
  // Update its own parameters
  this.mName            = pData.friendlyName;
  this.mUuid            = pData.uuid;
  this.mIpAddress       = pData.ipAddress;
  this.mHardwareVersion = this.formatHardwareVersion(pData.hardwareVersion);
};

Controller.prototype.toJson = function() {
  return {
    'category'  : 'config',
    'name'      : 'controller',
    'parameters': {
      'friendlyName'    : this.mName,
      'uuid'            : this.mUuid,
      'ipAddress'       : this.mIpAddress,
      'hardwareVersion' : this.mHardwareVersion
    }
  };
};

Controller.prototype.setDirty = function () {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  this.mManager.setDirty();
};

Controller.prototype.uniqueName = function(pNewName) {
  return _uniqueName(pNewName, this.mManager.mConfigurationList
    .map(function(pConfig) { return pConfig.getController() && pConfig.getController().getName(); }));
};

Controller.prototype.getDefaultName = function() {
  const l1BasedIndex = this.mManager.mConfigurationList.length + 1;
  return this.uniqueName('Controller #' + l1BasedIndex);
};

Controller.prototype.getDefaultIpAddress = function() {
  let lIpEnding = 2;
  let lIpAddress;

  do {
    lIpAddress = '192.168.0.' + lIpEnding;
    lIpEnding++;
  } while (!this.validateIpAddress(lIpAddress).success);

  return lIpAddress;
};

Controller.prototype.getName = function() {
  return this.mName;
};

Controller.prototype.getConfig = function() {
  return this.mConfig;
};

Controller.prototype.getFormattedName = function () {
  if(this.mManager.getConfigurationList().length === 1) { return this.mName; } // when there's only one controller, do not add a parent prefix
  return `${this.mManager.getParentConfiguration() === this.mConfig ? 'Parent:' : 'Child:'} ${this.mName}`;
};

Controller.prototype.formatHardwareVersion = function(pHardwareVersion) {
  // Note (AJ) This is required because the network discovery endpoints reports the full versions of hardware version.
  // and it needs to be formatted because the frontend and backend utilize a concatinated version.
  // Hence we have to convert V2A1 -> V2 || V2A1-1D -> V2-1D
  if ( pHardwareVersion.split('-')[0].length == 4) {
    const l_V2 = pHardwareVersion.slice(0, 2);
    const l_1D = pHardwareVersion.slice(4, pHardwareVersion.length);
    return `${l_V2}${l_1D}`.toUpperCase();
  }
  return pHardwareVersion;
};

Controller.prototype.setName = function(pName) {
  if (this.mName === pName) {
    return;
  }

  const lName = this.uniqueName(pName);
  this.mName = lName;
  this.notify('onNameChanged', this.mName);
  this.setDirty();
};

Controller.prototype.isAvailable = function() {
  return this.mIsControllerAvailable;
};

Controller.prototype.isParent = function() { return this.mManager.getParentControllerId() === this.mUuid; };

Controller.prototype.isHost = function() { return this.mManager.getHostControllerUuid() === this.mUuid; };

Controller.prototype.getUUID = function() {
  return this.mUuid;
};

Controller.prototype.setUUID = function(pUuid) {
  this.mUuid = pUuid;
  this.notify('onUuidChanged', this.mUuid);
  this.setDirty();
};

Controller.prototype.getIpAddress = function() {
  return this.mIpAddress;
};

Controller.prototype.setIpAddress = function(pIpAddress) {
  // IP adress of controllers must be unique
  if( !this.validateIpAddress(pIpAddress).success ) {
    return false;
  }

  this.mIpAddress = pIpAddress;
  this.notify('onIpAddressChanged', this.mIpAddress);
  this.setDirty();
  return true;
};

Controller.prototype.validateIpAddress = function(pIpAddress) {
  if (pIpAddress == 'localhost') {
    return { success: false, reason: 'The IP address localhost is not a valid option' };
  }

  const lIsValidIPv4 = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$/.test(pIpAddress);
  if (!lIsValidIPv4) { return { success: false, reason: `The IP address "${pIpAddress}" is not a valid IPv4 address` }; }

  const lThis = this;

  const lSameExactIPFindFunc = function(pConfig) {
    if (lThis.getUUID() === pConfig.getController().getUUID()) return false;
    return pConfig.getController().getIpAddress() === pIpAddress;
  };

  if (this.mManager.getConfigurationList().find(lSameExactIPFindFunc)) {
    return { success: false, reason: `The IP address "${pIpAddress}" is already being used by another controller` };
  }

  const lControllerNetworkInfo = this.mManager.mDetectedControllers.find(controllerInfo => controllerInfo.ipAddresses.includes(pIpAddress));
  const lSharedIPFindFunc = function(pConfig) {
    if (lThis.getUUID() === pConfig.getController().getUUID()) return false;
    return lControllerNetworkInfo.ipAddresses.includes(pConfig.getController().getIpAddress());
  };

  if (lControllerNetworkInfo && this.mManager.getConfigurationList().find(lSharedIPFindFunc)) {
    return { success: false, reason: `The IP address "${pIpAddress}" belongs to another configured controller` };
  }

  return { success: true };
};

Controller.prototype.getHardwareVersion = function() {
  return this.mHardwareVersion;
};

Controller.prototype.setHardwareVersion = function(pHardwareVersion) {
  if ( this.getNumberOfDrives() !== this.getNumberOfDrives(pHardwareVersion) || pHardwareVersion !== this.mHardwareVersion) {
    this.mHardwareVersion = pHardwareVersion;
    this.mConfig.updateAxisColors();
    this.mConfig.resetColors();
    this.notify('onHardwareVersionChanged', this.mHardwareVersion);
    this.setDirty();
  }
};

Controller.prototype.isHardwareV2 = function(pHardwareVersion) {
  if (pHardwareVersion) { return /v2/i.test(pHardwareVersion); }
  return /v2/i.test(this.getHardwareVersion());
};

Controller.prototype.isHardware_1D = function (pHardwareVersion) {
  if (pHardwareVersion) { return /-1D$/i.test(pHardwareVersion); }
  return /-1D$/i.test(this.getHardwareVersion());
};

/**
 * @return {Number} The number of drives depending on the HwVersion
 *         v1         : 3 drives
 *         v2 or v2xx : 4 drives
 *         v2xx-1d    : 1 drive
 */
Controller.prototype.getNumberOfDrives = function(pHardwareVersion) {
  if (pHardwareVersion) { return this.isHardwareV2(pHardwareVersion) ? (this.isHardware_1D(pHardwareVersion) ? 1 : 4) : 3; }
  return this.isHardwareV2() ? (this.isHardware_1D() ? 1 : 4) : 3;
};
